import React, { useState, useEffect, useCallback } from 'react';
import { Square, History, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });

const JobStatus = ({ job }) => {
  if (!job || job.status === 'none') return null;
  const active = ['queued', 'running', 'resuming'].includes(job.status);
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className={`rounded-lg p-3 text-sm ${['error', 'cancelled'].includes(job.status) ? 'bg-red-50 text-red-700' : active ? 'bg-blue-50 text-blue-800' : 'bg-green-50 text-green-800'}`} data-testid="history-import-status">
      <p className="font-medium flex items-center gap-1.5">
        {active ? <RefreshCw size={14} className="animate-spin" /> : ['error', 'cancelled'].includes(job.status) ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
        {active ? (job.phase || `Importando de ${job.source_email}…`) : ['error', 'cancelled'].includes(job.status) ? job.error : `Importación terminada (${job.source_email})`}
      </p>
      {job.total > 0 && (
        <>
          <div className="h-1.5 bg-white rounded-full mt-2"><div className="h-1.5 rounded-full bg-current transition-all" style={{ width: `${pct}%` }} /></div>
          <p className="text-xs mt-1.5" data-testid="history-import-counts">
            {job.done}/{job.total} correos · {job.created} facturas importadas · {job.duplicates} posibles duplicados · {job.unrecognized || 0} no reconocidas{job.rejected ? ` · ${job.rejected} descartados (no eran factura)` : ''}{job.skipped ? ` · ${job.skipped} ya procesados` : ''}{job.errors ? ` · ${job.errors} errores` : ''}
          </p>
        </>
      )}
      {job.already_done > 0 && <p className="text-xs mt-1" data-testid="history-import-already">{job.already_done} de {job.found} correos ya estaban leídos de antes y se han saltado sin volver a abrirlos.</p>}
      {job.status === 'done' && job.total === 0 && job.already_done > 0 && <p className="text-xs mt-1">No había correos nuevos que importar.</p>}
      {job.status === 'done' && job.total === 0 && !job.already_done && <p className="text-xs mt-1">No se encontraron correos con adjuntos desde {job.since}.</p>}
    </div>
  );
};

export const GmailHistoryImport = ({ onFinished }) => {
  const [form, setForm] = useState({ email: '', app_password: '', since: '2026-01-01', retry_empty: false });
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/gastos/gmail/import-history/latest`, { headers: headers() });
    if (res.ok) setJob(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = ['queued', 'running', 'resuming'].includes(job?.status);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active, load]);

  useEffect(() => {
    if (job?.status === 'done') onFinished?.();
  }, [job?.status, onFinished]);

  const stop = async () => {
    if (!window.confirm('¿Detener ahora? Lo ya procesado se mantiene.')) return;
    const res = await fetch(`${API_URL}/api/admin/gastos/gmail/import-history/cancel`, { method: 'POST', headers: headers() });
    if (!res.ok) return toast.error('No se pudo detener');
    toast.success('Detenida');
    load();
  };

  const start = async () => {
    setStarting(true);
    try {
      const body = { since: form.since, retry_empty: form.retry_empty };
      if (form.email && form.app_password) Object.assign(body, { email: form.email, app_password: form.app_password });
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/import-history`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'No se pudo iniciar');
      setJob(j);
      setForm(f => ({ ...f, app_password: '' }));
      toast.success('Importación iniciada. Puede tardar varios minutos.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid="gmail-history-import">
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <History size={18} className="text-gray-600" />
        <h3 className="font-semibold text-gray-900">Importar facturas antiguas de Gmail</h3>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs text-gray-500">
          Lee todos los correos con adjuntos desde la fecha indicada (también los leídos y archivados), sin reenviar nada.
          Déjalo vacío para usar el Gmail conectado, o pon otra cuenta con su contraseña de aplicación: no se guarda.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input type="email" placeholder="otro.correo@gmail.com (opcional)" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={active} data-testid="history-import-email" />
          <Input type="password" placeholder="Contraseña de aplicación" value={form.app_password}
            onChange={e => setForm(f => ({ ...f, app_password: e.target.value }))} disabled={active} data-testid="history-import-password" />
          <Input type="date" value={form.since} onChange={e => setForm(f => ({ ...f, since: e.target.value }))} disabled={active} data-testid="history-import-since" />
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer" data-testid="history-import-retry-label">
          <input type="checkbox" className="mt-0.5" checked={form.retry_empty} onChange={e => setForm(f => ({ ...f, retry_empty: e.target.checked }))} disabled={active} data-testid="history-import-retry-empty" />
          <span>Repasar también los correos ya leídos que no dieron ninguna factura <span className="text-xs text-gray-500">(más lento; úsalo si faltan facturas)</span></span>
        </label>
        <Button onClick={start} disabled={starting || active || (form.email && !form.app_password)} className="w-full sm:w-auto" data-testid="history-import-start-btn">
          {starting ? <RefreshCw size={14} className="animate-spin mr-2" /> : <History size={14} className="mr-2" />}
          Importar desde {form.since.split('-').reverse().join('/')}
        </Button>
        {active && (
          <Button onClick={stop} variant="outline" className="ml-2 border-gray-300" data-testid="history-import-stop-btn">
            <Square size={14} className="mr-2" /> Detener
          </Button>
        )}
        <JobStatus job={job} />
        <p className="text-[11px] text-gray-400">Solo se importan facturas de la lista autorizada de proveedores (por remitente, NIF o nombre fiscal); el resto queda en "No reconocidos". Las que ya existían se marcan como "posible duplicado".</p>
      </div>
    </div>
  );
};
